import { memo } from 'react';

import type { NetworkGraphNodeModel } from '../NetworkGraph';
import { NetworkGraphNode } from '../NetworkGraphNode';
import type {
  NetworkGraphPosition,
  NetworkGraphPositions,
} from '../utils/computeRadialLayout';
import type { LabelOffsets } from '../utils/resolveLabelCollisions';

export interface NetworkGraphNodeLayerProps {
  nodes: readonly NetworkGraphNodeModel[];
  positions: Readonly<NetworkGraphPositions>;
  connectedPositions: Map<string, NetworkGraphPosition[]>;
  rayAngles: Readonly<Record<string, number>>;
  labelOffsets: LabelOffsets;
  hoveredNodeId?: string | null;
  onNodeClick?: (nodeId: string) => void;
  onNodeHover: (nodeId: string, hovered: boolean) => void;
  suppressAfterDrag: (fn: () => void) => () => void;
}

const NetworkGraphNodeLayerComponent = ({
  nodes,
  positions,
  connectedPositions,
  rayAngles,
  labelOffsets,
  hoveredNodeId,
  onNodeClick,
  onNodeHover,
  suppressAfterDrag,
}: NetworkGraphNodeLayerProps) => (
  <>
    {nodes.map(node => {
      const position = positions[node.id];
      if (!position) {
        return null;
      }

      return (
        <NetworkGraphNode
          connectedPositions={connectedPositions.get(node.id)}
          isHovered={node.id === hoveredNodeId}
          key={node.id}
          labelOffset={labelOffsets[node.id]}
          node={node}
          rayAngle={rayAngles[node.id]}
          onClick={
            onNodeClick && node.isInteractive !== false
              ? suppressAfterDrag(() => onNodeClick(node.id))
              : undefined
          }
          onHover={hovered => onNodeHover(node.id, hovered)}
          position={position}
        />
      );
    })}
  </>
);

// Node geometry and GraphIcon rendering are skipped when only hover state moves.
export const NetworkGraphNodeLayer = memo(NetworkGraphNodeLayerComponent);
